import React from 'react'
import { string, array, object, bool } from 'prop-types'
import styles from './EventArticleGridSnippet.module.scss'
import Container from '@/component/Primitive/Container'
import Type from '@/component/Primitive/Type'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import SmartLink from '@/component/Primitive/SmartLink'
import { Highlight, ButtonGroup } from '@/component/Primitive/Events'
import cx from 'classnames'

export const EventArticleGridSnippet = ({
  title,
  subtitle,
  buttonGroup,
  articles,
  theme,
  includeThumbnails
}) => {
  if (!articles || articles.length < 1) return null

  // First article is the featured one
  const featuredArticle = articles[0]
  const remainingArticles = articles.slice(1, 5)

  return (
    <div className={cx(styles.EventArticleGridSnippet, styles[theme])}>
      <Container center size="wide" gutter>
        <div className={styles.Container}>
          <div className={styles.HeaderWrapper}>
            <div className={styles.TitleWrapper}>
              {title && (
                <Type
                  as="h2"
                  className={styles.Title}
                  size={['heading5', 'heading2']}
                  weight="bold"
                >
                  <Highlight
                    classNames={{
                      strong: styles.Highlight
                    }}
                  >
                    {title}
                  </Highlight>
                </Type>
              )}
              {subtitle && (
                <Type as="p" className={styles.Subtitle} size="body4">
                  {subtitle}
                </Type>
              )}
            </div>
            {!!buttonGroup?.length && (
              <ButtonGroup
                group={buttonGroup}
                className={styles.ButtonGroup}
                theme={theme}
              />
            )}
          </div>

          <div
            className={cx(
              styles.ArticlesGrid,
              !includeThumbnails && styles.NoThumbnails,
              articles.length === 1 && styles.Col1,
              articles.length === 2 && styles.Col2,
              articles.length === 3 && styles.Col3,
              articles.length === 4 && styles.Col4
            )}
          >
            <ArticleCard
              article={featuredArticle}
              isFeatured
              includeThumbnail={includeThumbnails}
            />

            {remainingArticles.map((article, i) => (
              <ArticleCard
                key={i}
                article={article}
                isFeatured={false}
                includeThumbnail={includeThumbnails}
              />
            ))}
          </div>
        </div>
      </Container>
    </div>
  )
}

EventArticleGridSnippet.propTypes = {
  title: string,
  subtitle: string,
  buttonGroup: array,
  articles: array,
  theme: string,
  includeThumbnails: bool
}

// Create article card component
const ArticleCard = ({ article, isFeatured, includeThumbnail = false }) => (
  <SmartLink
    href={article.link}
    className={cx(
      styles.ArticleCard,
      isFeatured && styles.FeaturedCard,
      !includeThumbnail && styles.NoThumbnail
    )}
  >
    {includeThumbnail && (
      <div className={styles.ImageWrapper}>
        <ResponsiveImage
          className={styles.Image}
          src={article.src}
          srcSet={article.srcSet}
          sizes={['(min-width: 960px) 20vw', '100vw']}
        />
      </div>
    )}
    <div className={styles.ContentWrapper}>
      <Type
        as="h3"
        size={['heading7', 'heading7']}
        weight="medium"
        className={styles.ArticleTitle}
      >
        {article.title}
      </Type>
      {article.category && (
        <div className={styles.Category}>{article.category}</div>
      )}
    </div>
  </SmartLink>
)

ArticleCard.propTypes = {
  article: object.isRequired,
  isFeatured: bool,
  includeThumbnail: bool
}

EventArticleGridSnippet.defaultProps = {
  theme: 'light',
  articles: [],
  includeThumbnails: true
}
